/**
 * @category Corners
 * @module SatelliteCorner
 */
import { CORNER_TYPES, SATELLITE_POINTERS } from "./constants";

import * as CornerTypes from "../types/CornerTypes";
import * as VuexTypes from "../types/VuexTypes";
import { hfInstance } from "../Cells";
import api from "../api";
import { rangeLabelToIndex } from "../../components/process-design/cell/Utils";

/**
 * @description Given a row keyPointer, reads all the data within that row and returns the column indexes for each data as key-value pairs.
 * @example {
 *   "ZEILE": 10,
 *   "NAMELAST": 11,
 *   "FTE": 12,
 *   "IN_Y": 13,
 *   "IN_M": 14,
 *   "EMPLOYEETYPE": 15,
 *   "SALARYGROUP": 16,
 *   "BONUS": 18,
 *   "X_INSERT": 19
 * }
 * @param {number} sheetHfId - The ID of the sheet to fetch the data from.
 * @param {CornerTypes.KeyPointer} keyPointer - The keyPointer used to determine the row data.
 * @returns {Object} - A map-like object with the key names as keys and column indexes as values.
 */
function getPointerValueColumnIndexes(sheetHfId, keyPointer) {
  const result = {};
  const pointerValues = hfInstance.getRangeValues({
    start: { sheet: sheetHfId, col: keyPointer.range[0], row: keyPointer.range[1] },
    end: { sheet: sheetHfId, col: keyPointer.range[2], row: keyPointer.range[3] },
  })[0];
  pointerValues.forEach((value, index) => {
    if (!value) return;
    result[value] = index + keyPointer.range[0];
  });
  return result;
}
const initialState = {
  sapData: null,
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {},
  actions: {
    /**
     * @function
     * @name module:SatelliteCorner.load
     * @description  load satellite corner Data
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     */
    async load({ commit, state, rootState, dispatch, getters, rootGetters }) {
      const currentSheetId = rootState.sheets.currentSheet.sheetId;
      /**@type {CornerTypes.Corners} */
      const corners = rootState.corners.corners;
      for (const [area, value] of Object.entries(corners)) {
        const { sheetHfId, columnRange, rowRange, rowKeyPointers, columnKeyPointers } = value;
        if (
          value.cornerType !== CORNER_TYPES.SATELLITE ||
          currentSheetId !== sheetHfId ||
          !getters.isSatelliteCornerValid(area)
        )
          continue;

        const readRowPointer = rowKeyPointers["read"][0];
        state.readColumnIndexes = getPointerValueColumnIndexes(sheetHfId, readRowPointer);

        const result = (await api.get("satellite/list")).data;
        state.sapData = result;
        const columnKeys = Object.keys(state.readColumnIndexes);
        const startRowPosition = getters.startRowPosition(area);
        hfInstance.suspendEvaluation();

        result.forEach((obj, index) => {
          columnKeys.forEach((columnKey) => {
            const columnIndex = state.readColumnIndexes[columnKey];
            // if (!zeileIndexes[Number(obj.ZEILE)]) return;
            hfInstance.setCellContents(
              { sheet: sheetHfId, col: columnIndex, row: startRowPosition + index },
              obj[columnKey]
            );
          });
        });

        hfInstance.resumeEvaluation();
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      }
      // dispatch("rangeStyle/persist", null, { root: true });
    },
    async save({ commit, state, rootState, dispatch, getters, rootGetters }, { tableName, rowsChanged }) {
      try {
        const data = {};
        rowsChanged.forEach((rowIndex) => {
          if (!state.sapData[rowIndex]) return;
          data[rowIndex] = {
            original: state.sapData[rowIndex],
            changed: {},
          };
        });

        let table = rootGetters["table/tableByName"](tableName);
        if (!table || !state.readColumnIndexes) return;

        const tableData = rootGetters["corners/getNamedExpressionAsArray"](`${tableName}..data`);
        const tableRange = rangeLabelToIndex(table.tableRange);
        if (!tableRange) return;
        rowsChanged.forEach((rowIndexInTable) => {
          Object.entries(state.readColumnIndexes).forEach(([key, columnIndex]) => {
            if (columnIndex < tableRange[0] || columnIndex > tableRange[2]) return;
            const columnIndexInTable = columnIndex - tableRange[0];
            const value = tableData[rowIndexInTable][columnIndexInTable];
            data[rowIndexInTable]["changed"][key] = String(value);
          });
        });
        const result = await api.patch(`/satellite/update`, Object.values(data));
      } catch (error) {
        console.log("error", error);
      }
    },
    async resetState({ commit, state, rootState, dispatch }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    /**
     * @callback StartRowResolver
     * @param {string} area - The corner ID (e.g. "Style_01", "Formula_02").
     * @returns {number} The starting row index of the data section for the given corner.
     */

    /**
     * @function
     * @name module:SatelliteCorner.startRowPosition
     * @description Gets the row index where the actual data starts for the given corner.
     *
     * All rows before this index are reserved for corner (meta) data.
     *
     * @returns { StartRowResolver}
     * A function that takes a corner ID and returns the starting row index of the data section.
     */
    startRowPosition: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const columnKeyPointers = corner.columnKeyPointers;
      const rowKeyPointer = columnKeyPointers[SATELLITE_POINTERS.START][0];
      const range = rowKeyPointer.range;

      const data = hfInstance.getRangeValues({
        start: { sheet: corner.sheetHfId, col: range[0], row: range[1] },
        end: { sheet: corner.sheetHfId, col: range[2], row: range[3] },
      });
      return Math.max(data.findIndex((item) => !!item[0]) + range[1], corner.columnRange[3] + 1);
    },
    /**
     * @callback ZeileResolver
     * @param {string} area - The corner ID (e.g. "Style_01", "Formula_02").
     * @returns { object } contain zeile number as key and row index as value
     */
    /**
     * @function
     * @name module:SatelliteCorner.readZeileData
     * @description
     * Resolves the mapping between Zeile numbers and their corresponding row indexes for a given corner area.
     *
     * In this context, "Zeile" acts as a start column key pointer. It determines where each row of satellite table data
     * should be inserted. The Zeile column must contain numeric values (e.g., 1, 2, 3...).
     * To skip a row, omit the Zeile number for that row.
     *
     * @returns {ZeileResolver} A resolver function that returns Zeile-to-row-index mappings for a given area.
     */
    readZeileData: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const readRowPointer = corner.rowKeyPointers["read"][0];
      const readColumnIndexes = getPointerValueColumnIndexes(corner.sheetHfId, readRowPointer);
      const startRowPosition = getters.startRowPosition(area);
      const zeileColumnIndex = readColumnIndexes.ZEILE;
      const zeileRange = [zeileColumnIndex, startRowPosition, zeileColumnIndex, corner.rowRange[3]];

      const data = hfInstance.getRangeValues({
        start: { sheet: corner.sheetHfId, col: zeileRange[0], row: zeileRange[1] },
        end: { sheet: corner.sheetHfId, col: zeileRange[2], row: zeileRange[3] },
      });
      const indexes = {};

      data.forEach((data, index) => {
        if (!data[0] || isNaN(data[0])) return;
        indexes[data] = index + startRowPosition;
      });

      return indexes;
    },
    /**
     * @callback SatteliteKeyPointerValidator
     * @param {string} area - The corner ID to validate (e.g., "Formula_01", "Style_03").
     * @returns {boolean} Returns `true` if the satellite key pointer setup is valid, otherwise `false`.
     */
    /**
     * @function
     * @name module:SatelliteCorner.isSatelliteCornerValid
     *
     * @description Checks if the satellite key pointer is valid for the given area.
     *
     * For a satellite corner to be considered valid, it must include:
     * - A `START` key in the column key pointers
     * - A `READ` key in the row key pointers
     *
     * @returns {SatteliteKeyPointerValidator}
     * A function that takes a corner ID and returns `true` if the satellite key pointer is valid; otherwise, `false`.
     */
    isSatelliteCornerValid: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      return corner.columnKeyPointers[SATELLITE_POINTERS.START] && corner.rowKeyPointers[SATELLITE_POINTERS.READ];
    },
  },
};
